import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, 
  Camera, 
  Video, 
  Loader2, 
  X,
  Image as ImageIcon,
  Film,
  Trash2,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const SURVEY_TYPES = [
  'General',
  'Structural',
  'Electrical',
  'Plumbing',
  'Roof',
  'Foundation',
  'Pre-start',
  'Progress',
  'Completion',
  'Defect'
];

// Thumbnail component that fetches the data_url
function MediaThumbnail({ media, surveyId, api }) {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (media.file_type === 'image') {
      fetchImage();
    } else {
      setLoading(false);
    }
  }, [media.id]);

  const fetchImage = async () => {
    try {
      const response = await api.get(`/portal/surveys/${surveyId}/media/${media.id}`);
      setImageData(response.data.data_url);
    } catch (err) {
      console.error('Failed to load thumbnail:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="aspect-square bg-[#1E2530] rounded-xl flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (media.file_type !== 'image' || error || !imageData) {
    return (
      <div className="aspect-square bg-[#1E2530] rounded-xl flex flex-col items-center justify-center relative">
        {media.file_type === 'video' ? (
          <Film className="w-10 h-10 text-gray-500" />
        ) : (
          <ImageIcon className="w-10 h-10 text-gray-500" />
        )}
        <span className="absolute bottom-0 left-0 right-0 text-xs text-white truncate bg-gradient-to-t from-black/80 to-transparent px-2 py-2">
          {media.filename}
        </span>
      </div>
    );
  }

  return (
    <div className="aspect-square bg-[#1E2530] rounded-xl overflow-hidden relative group">
      <img 
        src={imageData}
        alt={media.filename}
        className="w-full h-full object-cover"
      />
      <span className="absolute bottom-0 left-0 right-0 text-xs text-white truncate bg-gradient-to-t from-black/80 to-transparent px-2 py-2">
        {media.filename}
      </span>
    </div>
  );
}

export function PortalSurveys() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [projects, setProjects] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedSurvey, setExpandedSurvey] = useState(null);
  const [formData, setFormData] = useState({
    project_id: '',
    survey_type: 'General',
    notes: ''
  });
  const [pendingMedia, setPendingMedia] = useState([]);
  const fileInputRef = useRef(null);
  
  // Check if user is a surveyor - they get access to all live projects
  const isSurveyor = user?.user_type === 'surveyor' || user?.roles?.includes('surveyor');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Surveyors get all live projects, others get only their assigned projects
      const projectsEndpoint = isSurveyor ? '/portal/all-live-projects' : '/portal/my-projects';
      
      const [surveysRes, projectsRes] = await Promise.all([
        api.get('/portal/surveys'),
        api.get(projectsEndpoint)
      ]);
      setSurveys(surveysRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newMedia = [];
    
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        toast.error(`${file.name} is not a valid image or video`);
        continue;
      }

      // Check size (10MB for images, 50MB for videos)
      const maxSize = file.type.startsWith('video/') ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large (max ${maxSize / (1024 * 1024)}MB)`);
        continue;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      newMedia.push({
        id: crypto.randomUUID(),
        file,
        type: file.type.startsWith('video/') ? 'video' : 'image',
        name: file.name,
        size: file.size,
        previewUrl
      });
    }

    setPendingMedia(prev => [...prev, ...newMedia]);
    e.target.value = ''; // Reset input
  };

  const removePendingMedia = (id) => {
    setPendingMedia(prev => {
      const item = prev.find(m => m.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter(m => m.id !== id);
    });
  };

  const handleSubmit = async () => {
    if (!formData.project_id || !formData.notes.trim()) {
      toast.error('Please select a project and add notes');
      return;
    }

    setSubmitting(true);
    try {
      // Create survey
      const surveyRes = await api.post('/portal/surveys', {
        project_id: formData.project_id,
        survey_type: formData.survey_type.toLowerCase(),
        notes: formData.notes
      });

      const surveyId = surveyRes.data.id;

      // Upload media files
      if (pendingMedia.length > 0) {
        setUploading(true);
        for (const media of pendingMedia) {
          const formDataUpload = new FormData();
          formDataUpload.append('file', media.file);
          
          try {
            await api.post(`/portal/surveys/${surveyId}/upload`, formDataUpload, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadError) {
            console.error(`Failed to upload ${media.name}:`, uploadError);
            toast.error(`Failed to upload ${media.name}`);
          }
        }
        setUploading(false);
      }

      toast.success('Survey created successfully');
      setDialogOpen(false);
      setFormData({ project_id: '', survey_type: 'General', notes: '' });
      setPendingMedia([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create survey');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Site Surveys</h1>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-12 px-5 text-base font-semibold active:scale-95 transition-transform"
          data-testid="new-survey-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Survey
        </Button>
      </div>

      {/* Surveys List */}
      {surveys.length === 0 ? (
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-8 text-center">
            <Camera className="w-16 h-16 mx-auto text-gray-500 mb-4" />
            <p className="text-gray-400 mb-5 text-lg">No surveys yet</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-14 px-6 text-base font-semibold active:scale-95 transition-transform"
            >
              Create Your First Survey
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {surveys.map(survey => (
            <Card key={survey.id} className="bg-[#2A3240] border-white/10 overflow-hidden">
              <CardContent className="p-0">
                <div 
                  className="p-5 cursor-pointer active:bg-[#333d4d] transition-colors"
                  onClick={() => setExpandedSurvey(expandedSurvey === survey.id ? null : survey.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="text-white font-semibold text-base">{survey.project_ref}</span>
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs capitalize">
                          {survey.survey_type}
                        </Badge>
                      </div>
                      <p className="text-gray-400 text-sm line-clamp-2">{survey.notes}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span>{format(parseISO(survey.created_at), 'dd MMM yyyy HH:mm')}</span>
                        {survey.media?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <ImageIcon className="w-4 h-4" />
                            {survey.media.length} files
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="min-h-[44px] min-w-[44px] flex items-center justify-center">
                      {expandedSurvey === survey.id ? (
                        <ChevronUp className="w-6 h-6 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedSurvey === survey.id && (
                  <div className="px-5 pb-5 pt-0 border-t border-white/10">
                    <p className="text-gray-300 text-sm my-4">{survey.notes}</p>
                    
                    {/* Media Grid - With Thumbnails */}
                    {survey.media?.length > 0 && (
                      <div className="grid grid-cols-3 gap-3">
                        {survey.media.map(media => (
                          <MediaThumbnail 
                            key={media.id}
                            media={media}
                            surveyId={survey.id}
                            api={api}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Survey Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">New Site Survey</DialogTitle>
            <DialogDescription className="text-gray-400">
              Record a site survey with notes and photos
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 pt-4">
            <div className="space-y-2">
              <Label className="text-gray-300 text-base">Project *</Label>
              <Select 
                value={formData.project_id} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, project_id: v }))}
              >
                <SelectTrigger className="h-14 bg-[#1E2530] border-white/20 text-white text-base" data-testid="survey-project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {projects.map(project => (
                    <SelectItem 
                      key={project.id} 
                      value={project.id}
                      className="text-white focus:bg-[#F5A623]/20 py-3"
                    >
                      {project.project_ref} - {project.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300 text-base">Survey Type</Label>
              <Select 
                value={formData.survey_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, survey_type: v }))}
              >
                <SelectTrigger className="h-14 bg-[#1E2530] border-white/20 text-white text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {SURVEY_TYPES.map(type => (
                    <SelectItem 
                      key={type} 
                      value={type}
                      className="text-white focus:bg-[#F5A623]/20 py-3"
                    >
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300 text-base">Notes *</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Describe what you observed..."
                rows={4}
                className="bg-[#1E2530] border-white/20 text-white resize-none text-base"
                data-testid="survey-notes-input"
              />
            </div>

            {/* Media Upload */}
            <div className="space-y-3">
              <Label className="text-gray-300 text-base">Photos/Videos</Label>
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-14 border-white/20 border-dashed text-gray-300 active:scale-[0.98] transition-transform"
              >
                <Camera className="w-5 h-5 mr-2" />
                Tap to Add Media
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {/* Pending Media Preview */}
              {pendingMedia.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mt-3">
                  {pendingMedia.map(media => (
                    <div 
                      key={media.id}
                      className="relative aspect-square bg-[#1E2530] rounded-xl overflow-hidden"
                    >
                      {media.type === 'image' ? (
                        <img 
                          src={media.previewUrl} 
                          alt={media.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-10 h-10 text-gray-500" />
                        </div>
                      )}
                      <button
                        onClick={() => removePendingMedia(media.id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center active:scale-90 transition-transform shadow-lg"
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                      <span className="absolute bottom-0 left-0 right-0 text-xs text-white bg-gradient-to-t from-black/80 to-transparent px-2 py-2 truncate">
                        {formatFileSize(media.size)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDialogOpen(false);
                  setFormData({ project_id: '', survey_type: 'General', notes: '' });
                  setPendingMedia([]);
                }}
                className="flex-1 h-14 border-white/20 text-gray-300 text-base active:scale-[0.98] transition-transform"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={submitting || !formData.project_id || !formData.notes.trim()}
                className="flex-1 h-14 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] text-base font-semibold active:scale-[0.98] transition-transform"
                data-testid="save-survey-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    {uploading ? 'Uploading...' : 'Saving...'}
                  </>
                ) : (
                  'Save Survey'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
